import React from 'react';

import { useIsProductOfTheYear } from 'src/components/product-of-the-year/hooks';
import { GridCardProductOfTheYearAwardBadge } from 'src/components/product-of-the-year/badges';

import { GridCard } from './grid-card';
import { GridCardContent } from './grid-card-content';
import { GridCardHeader } from './grid-card-header';
import { GridCardOfferFooter } from './grid-card-offer-footer';
import { GridCardProductFooter } from './grid-card-product-footer';

import type { BaseProps } from '../../types';

export const ProductGridCard: React.FC<BaseProps> = props => {
  const {
    id,
    brand,
    category,
    diffPercentage,
    highlightedName,
    imageSize,
    imageSrc,
    name,
    previousPriceText,
    price,
    properties,
    rank,
    rating,
    reviewsCount,
    selectable,
    selected,
    shouldLazyLoadImage,
    store,
    type = 'product',
    link,
    onClick,
    className,
    container,
    background,
    onDeselect,
    onSelect,
    buttonAriaLabel,
    ActionArea,
    FooterArea,
    onRankInCategoryClick,
  } = props;

  const cardProps = {
    link,
    className,
    container,
    background,
    selected,
    selectable,
    onClick,
    onDeselect,
    onSelect,
    buttonAriaLabel,
    ActionArea,
    FooterArea,
  };

  const headerProps = {
    brand,
    diffPercentage,
    imageSize,
    imageSrc,
    name,
    selectable,
    selected,
    shouldLazyLoadImage,
  };

  const contentProps = {
    category,
    diffPercentage,
    highlightedName,
    name,
    price,
    properties,
    rank,
    rating,
    reviewsCount,
    store,
  };

  const footerProps = {
    type,
    rank,
    previousPriceText,
    category,
    store,
    link,
    onClick,
    onRankInCategoryClick,
  };

  // The card shouldn't be clickable for offers
  const isClickable = type === 'product';

  const productOfTheYearAward = useIsProductOfTheYear(
    type === 'product' ? id : undefined
  );

  return (
    <GridCard
      {...cardProps}
      isClickable={isClickable}
      FooterArea={FooterArea || <Footer {...footerProps} />}
      ActionArea={ActionArea}
      outlined
    >
      {productOfTheYearAward && (
        <GridCardProductOfTheYearAwardBadge
          badgeUrl={productOfTheYearAward.awardLogo}
          awardName={productOfTheYearAward.awardName}
          year={productOfTheYearAward.year}
        />
      )}
      <GridCardHeader {...headerProps} />
      <GridCardContent {...contentProps} />
    </GridCard>
  );
};

type FooterProps = Pick<
  BaseProps,
  | 'type'
  | 'rank'
  | 'previousPriceText'
  | 'category'
  | 'store'
  | 'link'
  | 'onClick'
  | 'onRankInCategoryClick'
>;

const Footer: React.FC<FooterProps> = props => {
  const {
    type,
    rank,
    previousPriceText,
    category,
    store,
    link,
    onClick,
    onRankInCategoryClick,
  } = props;

  const productFooterProps = {
    rank,
    previousPriceText,
    category,
    store,
    link,
    onClick,
    onRankInCategoryClick,
  };
  const offerFooterProps = { store, link, onClick };

  if (type === 'product') {
    return <GridCardProductFooter {...productFooterProps} />;
  }

  if (type === 'offer') {
    return <GridCardOfferFooter {...offerFooterProps} />;
  }

  return null;
};
